import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { CustomerService } from '../../services/customer.service';
import { ErrorService } from '../../services/error.service';
import { OrderService } from '../../services/order.service';
import { SnackbarService } from '../../services/snackbar.service';
import { SubscriptionService } from '../../services/subscription.service';
import { UserService } from '../../services/user.service';
import { Observable, of, skip, take } from 'rxjs';
import { searchSortOptions } from 'src/app/models/search-sort-options';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
	selector: 'app-parcel-modal',
	templateUrl: './parcel-modal.html',
	styleUrls: ['./parcel-modal.component.scss']
})

export class ParcelModalComponent implements OnInit, OnDestroy {
	public availableParcelTypes: any[] = [];
	public editingExistingParcel = false;
	public form: UntypedFormGroup;
	public order: any;
	public parcelMode: string = 'SINGLE';
	public requirePieces = false;
	public requireWeight = false;
	public customer: any;
	public subscriptions: any[] = [];
	public user: any;
	public refrigerationTypeSearchSortOptions: searchSortOptions = new searchSortOptions(10, 1, 'DESCRIPTION', 'ASCENDING', 'DESCRIPTION', '');
	public refrigerationTypes = [
	  {
		id: 1,
		description: 'FROZEN'
	  },
	  {
		id: 2,
		description: 'REFRIGERATED'
	  },
	  {
		id: 3,
		description: 'ROOM_TEMPERATURE'
	  },
	  {
		id: 4,
		description: 'AMBIENT'
	  }
	];

	constructor(public dialogRef: MatDialogRef<ParcelModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private _snackbarService: SnackbarService,
		private _customerService: CustomerService,
		private _errorService: ErrorService,
		private _orderService: OrderService,
		private _userService: UserService,
		private _formBuilder: UntypedFormBuilder,
		private _subscriptionService: SubscriptionService) {
	}

	changeParcelMode(event: MatRadioChange) {
		if (this.user.inetUser.miscData.hasOwnProperty('placeOrderView')) {
			this.user.inetUser.miscData.placeOrderView.parcelMode = event.value;
		} else {
			this.user.inetUser.miscData['placeOrderView'] = {
				parcelMode: event.value
			}
		}
		this._userService.setInetUserMiscData(this.user.inetUser.miscData).subscribe({
			next: () => {
				this._userService.updateUser(this.user);
			}
		});
		this.parcelMode = event.value;
		this.setValidatorsBasedOnMode();
		this.resetParcelData();
	}

	closeModal(response) {
		this.dialogRef.close(response);
	}

	compareFn(optionOne, optionTwo): boolean {
		if (optionOne && optionTwo) {
			return optionOne.id === optionTwo.id;
		} else {
			return false;
		}
	}

	createBarcode() {
		this.form.get('reference').setValue(`X-${this.user?.cxtId}-${Date.now()}`);
	}

	createForm() {
		this.form = this._formBuilder.group({
			type: null,
			weight: null,
			height: null,
			width: null,
			length: null,
			reference: '',
			pieces: 1,
			generateQuantity: [1, [Validators.min(1), Validators.max(100)]],
			refrigeration: null,
			userField1: null,
			comment: ''
		});

		this.setValidatorsBasedOnMode();
		this.subscriptions.push(this.form.get('type').valueChanges
			.pipe(distinctUntilChanged((a,b) => JSON.stringify(a) === JSON.stringify(b)))
			.subscribe((value) => {
				this.setParcelDefaults(value);
			}
		));
	}

	setValidatorsBasedOnMode() {
		const referenceControl = this.form.get('reference');
		if (referenceControl) {
			referenceControl.clearValidators();
			if (this.parcelMode !== 'GENERIC' || this.editingExistingParcel) {
				referenceControl.setValidators([Validators.required]);
			} else {
				referenceControl.setValidators([]);
			}
			referenceControl.updateValueAndValidity();
		}

		const generateQuantityControl = this.form.get('generateQuantity');
		if (generateQuantityControl) {
			generateQuantityControl.clearValidators();
			if (this.parcelMode === 'GENERIC' && !this.editingExistingParcel) {
				generateQuantityControl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
			} else {
				generateQuantityControl.setValidators([Validators.min(1), Validators.max(100)]);
			}
			generateQuantityControl.updateValueAndValidity();
		}
	}

	ngOnDestroy(): void {
		this._subscriptionService.unSubscribe(this.subscriptions);
	}

	ngOnInit(): void {
		this.editingExistingParcel = this.data.parcel != null;
		this.user = this._userService.getUser();
		if (this.user.inetUser.miscData.hasOwnProperty('placeOrderView')) {
			this.parcelMode = this.user.inetUser.miscData.placeOrderView.parcelMode;
		}
		this.order = this.data.order;
		this.createForm();

		let customer$ = this.data?.custId ? this._customerService.getCustomer(this.data?.custId) : of(this._customerService.getSelectedCustomer());
		customer$.subscribe({
			next: (customer) => {
				this.customer = customer;
				if (this.user.globalSettings.requirePiecesForOrders || this.customer.fieldRequiredSettings.piecesRequired) {
					this.requirePieces = true;
					this.form.get('pieces').setValidators(Validators.required);
				}
				if (this.user.globalSettings.requireWeightForOrders || this.customer.fieldRequiredSettings.weightRequired) {
					this.requireWeight = true;
					this.form.get('weight').setValidators(Validators.required);
				}
				if (this.user?.globalSettings?.requireParcelTypeForOrders) {
					this.form.get('type').setValidators(Validators.required);
				}
				if (this.customer?.fieldRequiredSettings?.requireParcelDims) {
					this.form.get('length').setValidators(Validators.required);
					this.form.get('width').setValidators(Validators.required);
					this.form.get('height').setValidators(Validators.required);
				}

				// Handle User Field 1 validation based on enableUserField1ForParcels flag
				const userField1Control = this.form.get('userField1');
				if (userField1Control) {
					if (this.customer?.enableUserField1ForParcels) {
						userField1Control.clearValidators();
						userField1Control.setValidators([Validators.required, Validators.maxLength(32)]);
					} else {
						userField1Control.clearValidators();
						userField1Control.setValidators([Validators.maxLength(32)]);
					}
					userField1Control.updateValueAndValidity();
				}

				this._orderService.getParcelTypes(this.customer.id).subscribe((response) => {
					this.availableParcelTypes = response;
					if (this.data.parcel != null) {
						this.setParcelData(this.data.parcel);
					} else if (this.availableParcelTypes.length > 0) {
						const { length, ...rest} = this.availableParcelTypes[0]; //length property in type object is failing validation, because we have a length property in the form
						this.form.get('length').setValue(length);
						this.form.get('type').setValue(rest);
						this.form.get('type').markAsTouched();
						this.form.get('type').updateValueAndValidity();
					}
					this.checkDimWt();
				}, (error) => {
					this._errorService.error(error, 'failed to getParcelTypes', 'modal');
				});
			}, error: (error) => {
				this._errorService.error(error, 'getCustomer failed', 'modal');
			}
		});
	}

	resetParcelData() {
		this.form.reset();
		//default values
		this.form.get('reference').setValue('');
		this.form.get('pieces').setValue(1);
		this.form.get('generateQuantity').setValue(1);
		this.form.get('comment').setValue('');
		if (this.availableParcelTypes.length > 0) {
			this.form.get('type').setValue(this.availableParcelTypes[0]);
		}
		this.checkDimWt();
	}

	checkDimWt() {
		if (this.form.get('type')) {
			let length = this.form.get('length').value;
			let width = this.form.get('width').value;
			let height = this.form.get('height').value;
			let currentType = this.form.get('type').value;
			if (currentType?.autoUseDimWt) {
				this.form.get('length').setValidators(Validators.required);
				this.form.get('width').setValidators(Validators.required);
				this.form.get('height').setValidators(Validators.required);
				this.form.get('weight').disable();
				let weight = Math.round(length * width * height / currentType.dimWtDenominator);
				let result = weight <= 1 ? 1 : weight;
				this.form.get('weight').setValue(result);
			} else {
				this.form.controls.height.removeValidators([Validators.required]);
				this.form.controls.length.removeValidators([Validators.required]);
				this.form.controls.width.removeValidators([Validators.required]);
				this.form.controls.weight.enable();
				this.form.controls.height.updateValueAndValidity();
				this.form.controls.length.updateValueAndValidity();
				this.form.controls.width.updateValueAndValidity();
				this.form.controls.weight.updateValueAndValidity();
			}
		}
	}

	saveParcel() {
		if (this.form.valid) {
			const parcels = [];
			if (this.parcelMode === 'GENERIC' && !this.editingExistingParcel) {
				let i = 0;
				let timeStamp = Date.now();
				while (i < this.form.get('generateQuantity')?.value) {
					const parcelTemplate = Object.assign({}, this.form.value);
					if (!parcelTemplate.type) {
						delete parcelTemplate.type;
					}
					timeStamp++;
					parcelTemplate.reference = `GEN-${this.customer.id}-${timeStamp}`;
					parcels.push(parcelTemplate);
					i++;
				}
			} else {
				const parcelTemplate = Object.assign({}, this.form.getRawValue());

				if (!this.editingExistingParcel) {
					if (this.order.parcels.find(x => x.reference.toLowerCase() === this.form.get('reference').value.toLowerCase())) {
						this._snackbarService.openSnackBar('You are attempting to create a duplicate parcel. Please enter a unique barcode.', 'Close', 5000);
						return;
					}
				}

				if (!parcelTemplate.type) {
					delete parcelTemplate.type;
				}

				if (this.parcelMode === 'SINGLE' || this.editingExistingParcel) {
					parcels.push(parcelTemplate);
				} else {
					this.order.parcels.push(parcelTemplate);
						this.form.get('reference').setValue('');
						this._snackbarService.openSnackBar('Success!  Parcel has been added to the order.', 'close', 3000, 'success');
				}
			}
			if (this.parcelMode !== 'MULTIPLE' || this.editingExistingParcel) {
				this.closeModal(parcels);
			}
		} else {
			this._snackbarService.openSnackBar('Please fill out all required fields.', 'Close', 3000);
		}
	}

	setParcelData(data) {
		this.form.patchValue(data);
	}

	setParcelDefaults(parcelType) {
		let pickedParcelType = this.availableParcelTypes.find((type) => type.id === parcelType?.id);
		if (pickedParcelType) {
			const { length, weight, width, height, ...rest} = pickedParcelType; // length property in type object is failing validation, because we have a length property in the form
			this.form.get('type').setValue({...rest, weight, width, height});
			this.form.patchValue({length, weight, width, height});
			this.form.updateValueAndValidity();
		}
	}

	searchRefrigerationTypes = (searchText: string): Observable<any[]> => {
		return of(this.refrigerationTypes.filter((type) => type.description.toLowerCase().includes(searchText.toLowerCase())))
			.pipe(skip((this.refrigerationTypeSearchSortOptions.pageNumber - 1) * this.refrigerationTypeSearchSortOptions.pageSize))
			.pipe(take(this.refrigerationTypeSearchSortOptions.pageSize));
	}

	setRefrigerationTypePageNumber(event) {
		this.refrigerationTypeSearchSortOptions.pageNumber = event;
	}

	setRefrigerationTypeFind(event) {
		this.form.get('refrigeration').setValue(event);
		this.form.get('refrigeration').markAsDirty();
	}

	mapRefrigerationType(refrigerationType) {
		return {name: refrigerationType.description, value: refrigerationType.description};
	}
}
